import {
  useForm,
  UseFormProps,
  FieldValues,
  SubmitHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { UseMutationResult } from '@tanstack/react-query';

interface UseGenericFormProps<TFormData extends FieldValues, TResponse> {
  schema: z.ZodType<TFormData, TFormData>;
  mutation: UseMutationResult<TResponse, AxiosError, TFormData>;
  onSuccess?: (response: TResponse) => void;
  onError?: (error: AxiosError) => void;
  formOptions?: UseFormProps<TFormData>;
}

export const useGenericForm = <TFormData extends FieldValues, TResponse>({
  schema,
  mutation,
  onSuccess,
  onError,
}: UseGenericFormProps<TFormData, TResponse>) => {
  const formMethods = useForm<TFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
  });

  const handleSubmit: SubmitHandler<TFormData> = (data) => {
    mutation.mutate(data, {
      onSuccess,
      onError: (error) => {
        console.error(error);
        onError?.(error);
      },
    });
  };

  return {
    ...formMethods,
    onSubmit: formMethods.handleSubmit(handleSubmit),
    isPending: mutation.isPending,
    errors: formMethods.formState.errors,
  };
};
